import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabase'
import { formatEuro, formatDate, MOIS_LABELS, TAUX_REUNION } from '../../utils/calculs'

export default function Reunions() {
  const now = new Date()
  const [annee, setAnnee] = useState(now.getFullYear())
  const [mois, setMois] = useState(now.getMonth() + 1)
  const [superviseurs, setSuperviseurs] = useState([])
  const [reunions, setReunions] = useState([])
  const [presencesParReunion, setPresencesParReunion] = useState({})
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  // Formulaire de nouvelle réunion
  const [date, setDate] = useState(now.toISOString().split('T')[0])
  const [titre, setTitre] = useState('')
  const [presents, setPresents] = useState({}) // { superviseurId: { present: bool, duree: '' } }

  // Liste des superviseurs actifs (une fois)
  useEffect(() => {
    supabase.from('profiles')
      .select('id, prenom, nom')
      .eq('role', 'superviseur')
      .eq('actif', true)
      .order('prenom')
      .then(({ data }) => setSuperviseurs(data || []))
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    const debut = `${annee}-${String(mois).padStart(2, '0')}-01`
    const dernierJour = new Date(annee, mois, 0).getDate()
    const fin = `${annee}-${String(mois).padStart(2, '0')}-${String(dernierJour).padStart(2, '0')}`

    const { data: reunionsData } = await supabase.from('reunions')
      .select('*')
      .gte('date', debut).lte('date', fin)
      .order('date')

    const reuns = reunionsData || []
    setReunions(reuns)

    if (reuns.length > 0) {
      const ids = reuns.map(r => r.id)
      const { data: presData } = await supabase.from('reunions_presences')
        .select('*, superviseur:superviseur_id(prenom, nom)')
        .in('reunion_id', ids)
      const grouped = {}
      for (const p of presData || []) {
        if (!grouped[p.reunion_id]) grouped[p.reunion_id] = []
        grouped[p.reunion_id].push(p)
      }
      setPresencesParReunion(grouped)
    } else {
      setPresencesParReunion({})
    }
    setLoading(false)
  }, [annee, mois])

  useEffect(() => { load() }, [load])

  function togglePresent(id) {
    setPresents(p => ({
      ...p,
      [id]: { present: !p[id]?.present, duree: p[id]?.duree || '' }
    }))
  }

  function setDuree(id, val) {
    setPresents(p => ({
      ...p,
      [id]: { present: p[id]?.present ?? true, duree: val }
    }))
  }

  function resetForm() {
    setDate(now.toISOString().split('T')[0])
    setTitre('')
    setPresents({})
  }

  async function handleCreate(e) {
    e.preventDefault()
    setMsg(null)

    const lignes = superviseurs
      .filter(s => presents[s.id]?.present)
      .map(s => ({ id: s.id, duree: parseFloat(presents[s.id]?.duree) }))

    if (lignes.length === 0) {
      setMsg({ type: 'error', text: 'Sélectionne au moins un superviseur présent.' })
      return
    }
    if (lignes.some(l => !l.duree || l.duree <= 0)) {
      setMsg({ type: 'error', text: 'Renseigne une durée valide pour chaque présent.' })
      return
    }

    setSaving(true)

    const { data: reunion, error: errReunion } = await supabase.from('reunions')
      .insert({ date, titre: titre || null, taux_horaire: TAUX_REUNION })
      .select().single()

    if (errReunion || !reunion) {
      setMsg({ type: 'error', text: 'Erreur lors de la création de la réunion.' })
      setSaving(false)
      return
    }

    const rows = lignes.map(l => ({
      reunion_id: reunion.id,
      superviseur_id: l.id,
      date,
      duree_heures: l.duree,
      montant: Math.round(l.duree * TAUX_REUNION * 100) / 100,
    }))

    const { error: errPres } = await supabase.from('reunions_presences').insert(rows)

    if (errPres) {
      setMsg({ type: 'error', text: 'Réunion créée mais erreur sur les présences.' })
    } else {
      setMsg({ type: 'success', text: `Réunion enregistrée (${rows.length} présent${rows.length > 1 ? 's' : ''}).` })
      setShowForm(false)
      resetForm()
      load()
    }
    setSaving(false)
  }

  async function handleDelete(reunion) {
    if (!window.confirm(`Supprimer la réunion du ${formatDate(reunion.date)} et ses présences ?`)) return
    await supabase.from('reunions').delete().eq('id', reunion.id)
    load()
  }

  const annees = [2024, 2025, 2026, 2027]
  const totalMois = Object.values(presencesParReunion)
    .flat()
    .reduce((a, p) => a + (p.montant || 0), 0)

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Réunions mensuelles</h2>
          <div className="page-header-sub">Rémunération {formatEuro(TAUX_REUNION)}/heure</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowForm(s => !s); setMsg(null) }}>
          {showForm ? 'Annuler' : '+ Nouvelle réunion'}
        </button>
      </div>

      {msg && <div className={`alert alert-${msg.type}`} style={{ marginBottom: 16 }}>{msg.text}</div>}

      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 20 }}>Nouvelle réunion</h3>
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0 20px' }}>
              <div className="form-group">
                <label>Date</label>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Titre (facultatif)</label>
                <input value={titre} onChange={e => setTitre(e.target.value)} placeholder="Ex : Réunion mensuelle superviseurs" />
              </div>
            </div>

            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Présents et durée</label>
            <table style={{ marginBottom: 16 }}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th>Superviseur</th>
                  <th className="num" style={{ width: 160 }}>Durée (heures)</th>
                  <th className="num" style={{ width: 120 }}>Montant</th>
                </tr>
              </thead>
              <tbody>
                {superviseurs.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--stone)', padding: 24 }}>Aucun superviseur actif</td></tr>
                ) : superviseurs.map(s => {
                  const p = presents[s.id]
                  const duree = parseFloat(p?.duree) || 0
                  return (
                    <tr key={s.id}>
                      <td>
                        <input type="checkbox" checked={!!p?.present} onChange={() => togglePresent(s.id)} />
                      </td>
                      <td><strong>{s.prenom} {s.nom}</strong></td>
                      <td className="num">
                        <input
                          type="number" min="0" step="0.25"
                          value={p?.duree || ''}
                          disabled={!p?.present}
                          onChange={e => setDuree(s.id, e.target.value)}
                          style={{ width: 100, textAlign: 'right' }}
                          placeholder="0"
                        />
                      </td>
                      <td className="num text-bordeaux">
                        {p?.present && duree > 0 ? formatEuro(duree * TAUX_REUNION) : <span className="text-muted">—</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" className="btn btn-bordeaux" disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer la réunion'}
              </button>
              <button type="button" className="btn btn-outline" onClick={() => { setShowForm(false); resetForm() }}>Annuler</button>
            </div>
          </form>
        </div>
      )}

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Année</label>
            <select value={annee} onChange={e => setAnnee(+e.target.value)}>
              {annees.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Mois</label>
            <select value={mois} onChange={e => setMois(+e.target.value)}>
              {MOIS_LABELS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading ? <div className="spinner" /> : (
        <>
          {reunions.length > 0 && (
            <div className="stats-grid" style={{ marginBottom: 20 }}>
              <div className="stat-card">
                <div className="s-label">Total réunions</div>
                <div className="s-value text-bordeaux">{formatEuro(totalMois)}</div>
                <div className="s-sub">{MOIS_LABELS[mois]} {annee}</div>
              </div>
              <div className="stat-card">
                <div className="s-label">Réunions</div>
                <div className="s-value">{reunions.length}</div>
              </div>
            </div>
          )}

          {reunions.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', color: 'var(--stone)', padding: 32 }}>
              Aucune réunion pour {MOIS_LABELS[mois]} {annee}
            </div>
          ) : reunions.map(r => {
            const pres = presencesParReunion[r.id] || []
            const totalR = pres.reduce((a, p) => a + (p.montant || 0), 0)
            return (
              <div className="card" key={r.id} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <strong>{formatDate(r.date)}</strong>
                    {r.titre && <span className="text-muted"> — {r.titre}</span>}
                  </div>
                  <button className="btn btn-sm btn-danger-ghost" onClick={() => handleDelete(r)}>Supprimer</button>
                </div>
                <table>
                  <thead>
                    <tr>
                      <th>Superviseur</th>
                      <th className="num">Durée</th>
                      <th className="num">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pres.map(p => (
                      <tr key={p.id}>
                        <td>{p.superviseur?.prenom} {p.superviseur?.nom}</td>
                        <td className="num">{p.duree_heures} h</td>
                        <td className="num text-bordeaux"><strong>{formatEuro(p.montant)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td><strong>Total</strong></td>
                      <td></td>
                      <td className="num text-bordeaux"><strong>{formatEuro(totalR)}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
